import type { SyntheticEvent } from 'react';
import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, TextField, useTheme } from '@mui/material';
import type { Filters } from '@repositories/softwareRepository/software.types';
import { useGetSoftwareList } from '@queryHooks/useSoftware';
import type { SoftwareSimpleModel } from '@repositories/softwareRepository/models/SoftwareSimpleModel';

type Props = {
  onChange: (product: SoftwareSimpleModel | null) => void;
  value: string | undefined;
  isEditable: boolean;
};

export const ProductAutocomplete = ({ onChange, value, isEditable }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [filter] = useState<Filters>({});
  const [selectedProduct, setSelectedProduct] = useState<SoftwareSimpleModel | null>(null);

  const { data: productFromServer } = useGetSoftwareList(filter);

  useEffect(() => {
    if (productFromServer?.content && value) {
      const initProduct = productFromServer.content.find(product => product.id === value);
      if (initProduct) {
        setSelectedProduct(initProduct);
      }
    } else if (!value) {
      setSelectedProduct(null);
    }
  }, [productFromServer?.content, value]);

  const handleChange = (_event: SyntheticEvent<Element, Event>, value: SoftwareSimpleModel | null) => {
    setSelectedProduct(value);
    onChange(value);
  };

  // 커스텀 필터링 함수
  const filterOptions = useCallback((options: SoftwareSimpleModel[], state: { inputValue: string }) => {
    const inputValue = state.inputValue.toLowerCase().trim();

    if (!inputValue) {
      return options;
    }

    const filtered = options.filter(option => {
      const name = (option.name || '').toLowerCase();
      const matches = name.includes(inputValue);

      return matches;
    });

    return filtered;
  }, []);

  return (
    <Autocomplete
      disabled={!isEditable}
      className="autocomplete"
      value={selectedProduct}
      size="medium"
      options={productFromServer?.content || []}
      filterOptions={filterOptions}
      getOptionKey={option => option.id}
      renderInput={params => (
        <TextField
          {...params}
          size="medium"
          placeholder={t('Common.Direct_Input')}
          sx={{
            '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderRadius: '2px 2px 0 0',
            },
            '& .MuiInputBase-input.Mui-disabled': {
              color: `${theme.colors['text-gray-light']}`,
            },
          }}
        />
      )}
      fullWidth
      isOptionEqualToValue={(option, value) => {
        if (!option || !value) return false;
        return option.id === value.id;
      }}
      getOptionLabel={product => product?.name || '-'}
      onChange={handleChange}
      ListboxProps={{
        style: {
          maxHeight: '328px',
        },
      }}
      componentsProps={{
        paper: {
          sx: {
            width: '100%',
            marginTop: 0,
            borderRadius: '0 0 2px 2px',
            boxShadow: '0',
            border: `1px solid ${theme.colors['border-gray-light']}`,
            borderWidth: '0 1px 1px 1px',
            overflow: 'hidden',
            '& .MuiList-root': {
              border: 'none',
              padding: '4px 0',
              '& .MuiMenuItem-root': {
                margin: '0 5px',
              },
            },
          },
        },
      }}
      sx={{
        '&.MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input': {
          padding: '7.5px 4px 7.5px 12px',
        },
      }}
    />
  );
};
